import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Icon, Modal, ModalContent, ModalHeader } from "semantic-ui-react";

import { PinLog } from "components";
import printJS from "lib/print-js";
import {
  ChecklistSection,
  HeaderSection,
  ImportantNotes,
  InterventionSection,
  PinSection,
  QuestionResultSection,
  WarrantyLogSection
} from "modules/AppointmentDetails/components/Interventions/components/PinOverview/components";
import "modules/AppointmentDetails/components/Interventions/components/PinOverview/PinOverview.scss";

import type { DropdownItemProps } from "components";
import type { Appointment, Intervention, PIN_ORDER_STATUSES, PIN_STATUSES, PIN_TYPES, Pin, WARRANTY_TYPES } from "models";
import type { OrderedGroupedCheck } from "modules/AppointmentDetails/hooks";

type PinOverviewProps = {
  showPrintOverview: boolean;
  pin?: Pin;
  pinLog?: Pin[];
  appointment?: Appointment;
  intervention?: Intervention;
  check?: OrderedGroupedCheck;
  pinStatusOptions: DropdownItemProps<PIN_STATUSES>[];
  pinTypeOptions: DropdownItemProps<PIN_TYPES>[];
  pinOrderOptions: DropdownItemProps<PIN_ORDER_STATUSES>[];
  pinWarrantyOptions: DropdownItemProps<WARRANTY_TYPES>[];
  onClose: () => void;
};

export const PinOverview = ({
  showPrintOverview,
  pin,
  onClose,
  appointment,
  intervention,
  pinLog,
  check,
  pinOrderOptions,
  pinStatusOptions,
  pinTypeOptions,
  pinWarrantyOptions
}: PinOverviewProps) => {
  const { t } = useTranslation();

  const [showCustomerInformation, setShowCustomerInformation] = useState(true);
  const [showActivityLog, setShowActivityLog] = useState(false);

  const questionResult = check?.question_items.find(qr => qr.id === pin?.question_result_id) ?? pin?.question_result;

  const onClickHandler = () =>
    printJS({
      printable: "pin-overview-content",
      type: "html",
      copyStyles: true,
      css: ["./PinOverview.css"],
      scanStyles: false,
      targetStyles: ["*"]
    });
  const handleToggleCustomerInformation = () => setShowCustomerInformation(!showCustomerInformation);
  const handleToggleActivityLog = () => setShowActivityLog(!showActivityLog);

  return (
    <Modal size="large" open={showPrintOverview} onClose={onClose} className="PinOverviewContent">
      <ModalHeader>
        <div className="modal-header-container">
          <div className="toggles-container">
            <Checkbox
              label={t("v8_show_customer_information", "Show customer information")}
              checked={showCustomerInformation}
              onChange={handleToggleCustomerInformation}
              toggle
            />
            <Checkbox label={t("v8_show_activity_log", "Show activity log")} checked={showActivityLog} onChange={handleToggleActivityLog} toggle />
          </div>
          <div className="buttons-container">
            <Button color="grey" className="-appointment-status" onClick={onClickHandler}>
              <Icon className="print black" />
              {t("v8_print", "Print")}
            </Button>
            <Button icon size="medium" onClick={onClose} className="light">
              <Icon name="cancel" size="large" className="cancel-icon" />
            </Button>
          </div>
        </div>
      </ModalHeader>
      <ModalContent scrolling id="pin-overview-content">
        <HeaderSection showCustomerInformation={showCustomerInformation} appointment={appointment} />
        <PinSection pin={pin} pinStatusOptions={pinStatusOptions} />
        {questionResult && <QuestionResultSection questionResult={questionResult} />}
        {intervention && <InterventionSection intervention={intervention} />}
        {(intervention || pin) && <ChecklistSection intervention={intervention} pin={pin} check={check} />}
        <ImportantNotes pinLog={pinLog} />
        {pinLog && (
          <div className="log-section">
            <h2 className="title"> Logs</h2>
            <PinLog
              pinLog={pinLog}
              pinTypeOptions={pinTypeOptions}
              pinOrderOptions={pinOrderOptions}
              pinStatusOptions={pinStatusOptions}
              pinWarrantyOptions={pinWarrantyOptions}
              showTitle={false}
              intervention={intervention}
            />
          </div>
        )}
        {showActivityLog && <WarrantyLogSection showCustomerInformation={showCustomerInformation} appointment={appointment} />}
      </ModalContent>
    </Modal>
  );
};
